import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {NavigationEnd, Router} from '@angular/router';
import {filter, Subscription} from 'rxjs';
import {env} from '../../environments/environment';

type MetrikaParams = Record<string, string | number | boolean | null | undefined>;

type MetrikaWindow = Window & {
    ym?: (...args: unknown[]) => void;
};

const ATTRIBUTION_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'yclid',
] as const;

@Injectable({providedIn: 'root'})
export class AnalyticsService implements OnDestroy {
    private initialized = false;
    private previousUrl = '';
    private routerSubscription?: Subscription;

    constructor(
        private router: Router,
        private title: Title,
        @Inject(DOCUMENT) private document: Document,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    initialize(): void {
        if (this.initialized || !isPlatformBrowser(this.platformId) || !env.METRIKA_ENABLED) {
            return;
        }

        this.initialized = true;
        this.previousUrl = window.location.href;
        this.routerSubscription = this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                const nextUrl = new URL(event.urlAfterRedirects, window.location.origin).href;

                if (nextUrl !== this.previousUrl) {
                    this.call('hit', nextUrl, {
                        referer: this.previousUrl,
                        title: this.title.getTitle(),
                    });
                }

                this.previousUrl = nextUrl;
            });
    }

    goal(name: string, params: MetrikaParams = {}): void {
        if (!isPlatformBrowser(this.platformId) || !env.METRIKA_ENABLED) {
            return;
        }

        this.call('reachGoal', name, {
            page: window.location.pathname,
            ...params,
        });
    }

    goalOnce(name: string, params: MetrikaParams = {}): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const storageKey = `motordesk_goal_${name}`;
        try {
            if (window.sessionStorage.getItem(storageKey)) {
                return;
            }
            window.sessionStorage.setItem(storageKey, '1');
        } catch {
        }

        this.goal(name, params);
    }

    registrationUrl(source: string): string {
        return this.lkUrl('/register', source);
    }

    demoUrl(source: string): string {
        return this.lkUrl('/demo', source);
    }

    externalLkUrl(rawUrl: string, source: string): string {
        try {
            const url = new URL(rawUrl);
            if (url.origin !== new URL(env.URL_LK).origin) {
                return rawUrl;
            }

            return this.decorateUrl(url, source).toString();
        } catch {
            return rawUrl;
        }
    }

    trackExternalAction(rawUrl: string, source: string, label: string): void {
        if (rawUrl.startsWith(env.URL_LK_REGISTER)) {
            this.goal('registration_click', {source, label});
            return;
        }

        if (rawUrl.startsWith(env.URL_LK_DEMO)) {
            this.goal('demo_click', {source, label});
            return;
        }

        if (rawUrl.startsWith(env.URL_LK)) {
            this.goal('lk_click', {source, label});
        }
    }

    ngOnDestroy(): void {
        this.routerSubscription?.unsubscribe();
    }

    private lkUrl(path: string, source: string): string {
        return this.decorateUrl(new URL(path, env.URL_LK), source).toString();
    }

    private decorateUrl(url: URL, source: string): URL {
        url.searchParams.set('source', source);

        if (isPlatformBrowser(this.platformId)) {
            url.searchParams.set('landing', window.location.pathname);
            const currentParams = new URLSearchParams(window.location.search);
            ATTRIBUTION_KEYS.forEach((key) => {
                const value = currentParams.get(key);
                if (value) {
                    url.searchParams.set(key, value);
                }
            });
        }

        return url;
    }

    private call(method: string, ...args: unknown[]): void {
        const metrikaWindow = this.document.defaultView as MetrikaWindow | null;
        metrikaWindow?.ym?.(env.METRIKA_COUNTER_ID, method, ...args);
    }
}
